/**
 * Hreflang Composable
 * Provides reactive hreflang functionality for Vue components
 */

import { ref, watch, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { hreflangService } from "@/services/hreflang.service";

export function useHreflang() {
	const route = useRoute();
	const store = useStore();
	const hreflangTags = ref([]);
	const isInitialized = ref(false);

	/**
	 * Generate hreflang tags for the current route
	 */
	const generateHreflangTags = () => {
		const languages = store.getters["languages/languages"];
		const languageCodes =
			languages.length > 0
				? languages.map((lang) => lang.two_digit_code)
				: null;

		const tags = hreflangService.generateHreflangTags(route, languageCodes);
		hreflangTags.value = tags;
		return tags;
	};

	/**
	 * Inject hreflang tags into document head
	 */
	const injectHreflangTags = () => {
		hreflangService.injectHreflangTags(hreflangTags.value);
	};

	/**
	 * Generate and inject hreflang tags
	 */
	const updateHreflangTags = () => {
		generateHreflangTags();
		injectHreflangTags();
	};

	/**
	 * Watch for route changes and update hreflang tags
	 */
	const watchRouteChanges = () => {
		watch(
			() => route.path,
			() => {
				updateHreflangTags();
			},
			{ immediate: true },
		);
	};

	/**
	 * Watch for language changes and update hreflang tags
	 */
	const watchLanguageChanges = () => {
		watch(
			() => store.getters["languages/languages"],
			() => {
				updateHreflangTags();
			},
			{ deep: true },
		);
	};

	/**
	 * Initialize hreflang functionality
	 */
	const initializeHreflang = () => {
		if (isInitialized.value) return;

		updateHreflangTags();
		watchRouteChanges();
		watchLanguageChanges();
		isInitialized.value = true;
	};

	/**
	 * Clean up hreflang tags
	 */
	const cleanupHreflang = () => {
		hreflangService.removeExistingHreflangTags();
	};

	// Auto-initialize when composable is used
	onMounted(() => {
		initializeHreflang();
	});

	// Clean up on unmount
	onUnmounted(() => {
		cleanupHreflang();
	});

	return {
		hreflangTags,
		isInitialized,
		generateHreflangTags,
		injectHreflangTags,
		updateHreflangTags,
		initializeHreflang,
		cleanupHreflang,
	};
}
